import { useId, useRef, useState, type ReactNode } from 'react';

import { cx } from '../lib/cx';
import { useOnClickOutside, useOnEscape } from '../lib/hooks';

export interface FilterOption {
  value: string;
  label: string;
  /** Optional tabular count shown on the right (Type menu). */
  count?: number;
}

export interface FilterChipProps {
  /** Dimension name, e.g. `Type`, `Location`. */
  name: string;
  options: readonly FilterOption[];
  /**
   * Selected values. Multi-select passes several; single-select passes zero or
   * one (empty = the implicit `All`).
   */
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  /** Multi-select stays open across picks; single closes on pick. */
  multi?: boolean;
}

/** Format the chip label: `Type · All` / `Type · Invoice` / `Type · 3 selected`. */
function chipLabel(name: string, options: readonly FilterOption[], selected: readonly string[]): string {
  if (selected.length === 0) return `${name} · All`;
  if (selected.length === 1) {
    const only = options.find((o) => o.value === selected[0]);
    return `${name} · ${only?.label ?? String(selected[0])}`;
  }
  return `${name} · ${String(selected.length)} selected`;
}

/** A single filter chip with its dropdown menu (design-reference §6.6). */
export function FilterChip({ name, options, selected, onChange, multi = false }: FilterChipProps): React.JSX.Element {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, open, () => {
    setOpen(false);
  });
  useOnEscape(open, () => {
    setOpen(false);
  });

  const active = selected.length > 0;

  const toggle = (value: string): void => {
    if (multi) {
      onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
    } else {
      onChange(value === '' ? [] : [value]);
      setOpen(false);
    }
  };

  return (
    <div className="ds-filter-anchor" ref={ref}>
      <button
        type="button"
        className={cx('ds-chip', active && 'ds-chip--active')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        {chipLabel(name, options, selected)}
        <span className="ds-chip__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <ul className="ds-filter-menu" id={menuId} role="menu" aria-label={name}>
          {!multi ? (
            <li>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={selected.length === 0}
                className="ds-menu__item"
                onClick={() => {
                  toggle('');
                }}
              >
                All
                {selected.length === 0 ? <span className="ds-menu__tick" aria-hidden="true">✓</span> : null}
              </button>
            </li>
          ) : null}
          {options.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role={multi ? 'menuitemcheckbox' : 'menuitemradio'}
                  aria-checked={checked}
                  className="ds-menu__item"
                  onClick={() => {
                    toggle(option.value);
                  }}
                >
                  <span aria-hidden="true">{checked ? (multi ? '☑' : '●') : multi ? '☐' : '○'}</span>
                  {option.label}
                  {option.count !== undefined ? (
                    <span className="ds-filter-count">{option.count.toLocaleString('en-GB')}</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export interface FilterBarProps {
  /** Chips and any other inline controls. */
  children: ReactNode;
  /** Show the `Clear all` link. */
  onClearAll?: () => void;
  /** Right-aligned context slot (e.g. "vs previous period · Save view"). */
  context?: ReactNode;
}

/** The filter bar container: `FILTERS` label, chips, clear-all, right context. */
export function FilterBar({ children, onClearAll, context }: FilterBarProps): React.JSX.Element {
  return (
    <div className="ds-filterbar" role="group" aria-label="Filters">
      <span className="ds-filterbar__label">FILTERS</span>
      {children}
      {onClearAll !== undefined ? (
        <button type="button" className="ds-linkbtn" onClick={onClearAll}>
          Clear all
        </button>
      ) : null}
      {context !== undefined ? (
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--t2)' }}>{context}</span>
      ) : null}
    </div>
  );
}
